import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Share2, Copy, MessageCircle, Instagram, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context";
import { shareProduct, whatsappShareUrl, buildShareText } from "@/lib/shareProduct";

/**
 * Reusable "share this product" control.
 * variant="icon"  → small circular icon button (product cards)
 * variant="full"  → labelled pill button (product detail page)
 * Falls back to a fixed bottom-sheet (WhatsApp / copy / Instagram hint) on
 * browsers without the Web Share API (desktop).
 */
export default function ShareButton({ product, variant = "icon", className = "" }) {
  const { lang } = useApp();
  const ar = lang === "ar";
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const res = await shareProduct(product, lang);
      if (res === "menu") setMenu(true);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    const { url } = buildShareText(product, lang);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(ar ? "تم نسخ الرابط 🌸" : "Link copied 🌸");
    } catch {
      toast.error(ar ? "تعذّر النسخ" : "Copy failed");
    }
    setMenu(false);
  };

  const closeMenu = (e) => { e?.stopPropagation?.(); setMenu(false); };

  return (
    <>
      {variant === "full" ? (
        <button onClick={onClick} className={`flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all active:scale-95 ${className}`}
                style={{ border: "1.5px solid var(--primary)", color: "var(--primary)" }}
                data-testid={`product-share-btn-${product.id}`}>
          <Share2 size={16} /> {ar ? "شارك الهدية" : "Share gift"}
        </button>
      ) : (
        <button onClick={onClick}
                className={`w-8 h-8 rounded-full backdrop-blur flex items-center justify-center transition-all active:scale-90 ${className}`}
                style={{ background: "rgba(255,255,255,0.9)", color: "var(--primary)" }}
                aria-label={ar ? "شارك المنتج" : "Share product"}
                data-testid={`product-share-btn-${product.id}`}>
          <Share2 size={15} />
        </button>
      )}

      {menu && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
             style={{ background: "rgba(0,0,0,0.45)" }} onClick={closeMenu} data-testid="share-fallback-sheet">
          <div dir={ar ? "rtl" : "ltr"} onClick={(e) => e.stopPropagation()}
               className="bg-white w-full sm:max-w-sm sm:rounded-3xl rounded-t-3xl p-5 space-y-3 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="font-bold" style={{ color: "var(--primary)" }}>{ar ? "شارك هذه الهدية" : "Share this gift"}</div>
              <button onClick={closeMenu} className="opacity-60 hover:opacity-100" data-testid="share-sheet-close"><X size={18} /></button>
            </div>
            <a href={whatsappShareUrl(product, lang)} target="_blank" rel="noopener noreferrer"
               onClick={() => setMenu(false)}
               className="flex items-center gap-3 rounded-2xl px-4 py-3 font-medium text-white" style={{ background: "#25D366" }}
               data-testid="share-whatsapp">
              <MessageCircle size={18} /> {ar ? "واتساب" : "WhatsApp"}
            </a>
            <button onClick={copyLink}
                    className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 font-medium" style={{ background: "var(--bg-subtle)", color: "var(--primary)" }}
                    data-testid="share-copy-link">
              <Copy size={18} /> {ar ? "نسخ الرابط" : "Copy link"}
            </button>
            <div className="flex items-start gap-3 rounded-2xl px-4 py-3 text-xs opacity-80" style={{ background: "linear-gradient(135deg,#fdf2f8,#fef9c3)" }}>
              <Instagram size={18} className="shrink-0 mt-0.5" style={{ color: "#d6249f" }} />
              <span>{ar ? "للانستغرام: انسخ الرابط ثم الصقه في قصتك أو رسائلك 🌸" : "For Instagram: copy the link, then paste it in your story or DMs 🌸"}</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
